<template>
  <v-dialog :value="value" max-width="1053px" @input="$emit('input', $event)">
    <v-card class="dialog-container">
      <div class="dialog-header">
        <div>
          <p class="dialog-title">{{ workflowLabel(workshopName) }} {{ adminText('workflowsAll.feedback_ef15f48', 'feedback') }}</p>
          <p class="dialog-subtitle">
            {{ adminText('workflowsAll.review_attendee_ratings_and_feedback_to_under_f335c6d', 'Review attendee ratings and feedback to understand how the session was received and identify opportunities for improvement.') }}
          </p>
        </div>
        <v-icon class="close-icon" @click="$emit('input', false)">close</v-icon>
      </div>

      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">{{ adminText('workflowsAll.reviews_b83c4cd', 'Reviews') }}</span>
          <span class="muted">({{ workflowLabel(total) }})</span>
          <feedback-stars class="panel-average" :value="average" />
          <span class="muted">({{ workflowLabel(average.toFixed(1)) }}/5.0)</span>
        </div>

        <spinner v-if="$apollo.loading" />

        <div v-else class="reviews">
          <div v-for="review in items" :key="review.id" class="review">
            <img v-if="avatarOf(review)" class="review-avatar" :src="avatarOf(review)" alt="">
            <div v-else class="review-avatar review-avatar--empty">
              <v-icon>person</v-icon>
            </div>
            <div class="review-body">
              <div class="review-head">
                <span class="review-name">{{ workflowLabel(nameOf(review)) }}</span>
                <span class="review-rating">
                  <feedback-stars :value="review.rating" />
                  <span class="muted">({{ workflowLabel(review.rating.toFixed(2)) }}/5.00)</span>
                </span>
              </div>
              <p v-if="review.comment" class="review-comment">&ldquo;{{ workflowLabel(review.comment) }}&rdquo;</p>
            </div>
          </div>
        </div>

        <div v-if="pageCount > 1" class="pager">
          <v-pagination v-model="page" :length="pageCount" :total-visible="5" />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
/**
 * COR-1684: the organiser's preview of a workshop's talk feedback.
 *
 * Figma w5wqtsXqhRLkqFBhbr2vit, 26919:96306 — a strip with the count and the average over
 * the whole workshop, then six reviews a page in two columns, then the pager. The strip's
 * numbers come from the query, not from `items`, so they do not change as you page.
 *
 * Opened from `workshop-form.vue`'s "See all the feedback" link, which only renders when
 * there is something behind it — hence no empty state here.
 */
import { GET_WORKSHOP_FEEDBACK } from '../../graphql/workshop.graphql.js'
import FeedbackStars from './workshop-feedback-stars.vue'
import Spinner from '../shared/spinner.vue'

/** Two columns x three rows, as the frame draws it. */
const PER_PAGE = 6

export default {
  mixins: [WorkflowDisplay],
  name: 'workshop-feedback-modal',
  components: { FeedbackStars, Spinner },
  props: {
    value: Boolean,
    eventId: {
      type: String,
      required: true,
    },
    workshopId: {
      type: String,
      required: true,
    },
    workshopName: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    page: 1,
    total: 0,
    average: 0,
    items: [],
  }),
  computed: {
    pageCount() {
      return Math.ceil(this.total / PER_PAGE)
    },
  },
  watch: {
    // Reopened after reviews were deleted, the old page could be past the end.
    value(open) {
      if (open) this.page = 1
    },
  },
  apollo: {
    getWorkshopFeedback: {
      query: GET_WORKSHOP_FEEDBACK,
      variables() {
        return {
          eventId: this.eventId,
          workshopId: this.workshopId,
          page: this.page,
          perPage: PER_PAGE,
        }
      },
      // Nothing to fetch until the organiser opens it.
      skip() {
        return !this.value
      },
      manual: true,
      result({ data, loading }) {
        if (loading || !data || !data.getWorkshopFeedback) return

        const { total, average, items } = data.getWorkshopFeedback
        this.total = total
        this.average = average
        this.items = items
      },
    },
  },
  methods: {
    nameOf({ attendee }) {
      const fields = (attendee && attendee.fields) || {}

      return `${fields['First name'] || ''} ${fields['Last name'] || ''}`.trim()
    },
    avatarOf({ attendee }) {
      return attendee && attendee.conferenceData && attendee.conferenceData.avatar
    },
  },
}
</script>

<style scoped>
.dialog-container {
  padding: 24px !important;
}

.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}

.dialog-title {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin: 0 0 12px 0;
}

.dialog-subtitle {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
  margin: 0;
}

.close-icon {
  cursor: pointer;
  color: #333 !important;
}

.panel {
  border: 1px solid #f5f5f5;
  border-radius: 12px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px;
  background: #f5f5f5;
}

.panel-title {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.03em;
  color: #333;
}

.panel-average {
  margin-left: 12px;
}

.muted {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
}

.reviews {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  padding: 16px;
}

.review {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px 0;
}

.review-avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.review-avatar--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.review-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.review-head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.review-rating {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.review-name {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  line-height: 1.5;
  color: #333;
}

.review-comment {
  font-family: 'Source Sans Pro', sans-serif;
  font-style: italic;
  font-size: 14px;
  line-height: 1.2;
  color: #666;
  margin: 0;
  overflow-wrap: anywhere;
}

.pager {
  display: flex;
  justify-content: flex-end;
  padding: 16px;
}

@media (max-width: 800px) {
  .reviews {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
